/**
 * Webview Event Types
 *
 * Defines all event types and their payload structures for communication
 * between the web app and native webview container.
 */

/**
 * Event types that can be sent TO native (web -> native)
 */
export enum WebviewSendEventType {
  /** Toggle the native pull-to-refresh gesture based on scroll position */
  SET_PULL_TO_REFRESH_ENABLED = 'SET_PULL_TO_REFRESH_ENABLED',
  /** Notify native that the user has logged out so it can clear its own session */
  LOGOUT = 'LOGOUT',
  /** Tell native which color to paint its system chrome (status bar, nav bar) so it matches the web theme */
  CHANGE_THEME_COLOR = 'CHANGE_THEME_COLOR',
}

/**
 * Payload for SET_PULL_TO_REFRESH_ENABLED event
 */
export interface SetPullToRefreshEnabledPayload {
  /** Whether the pull-to-refresh gesture should be enabled on the native side */
  enabled: boolean;
}

/**
 * Recognized reasons emitted with the LOGOUT event. Treated as an analytics-only
 * label by the native side; tightening to a closed union catches typos at compile
 * time. Add new values here when a new logout flow needs to be distinguished.
 */
export type LogoutReason = 'User initiated' | 'Auth error';

/**
 * Payload for LOGOUT event
 */
export interface LogoutPayload {
  /** Optional analytics-only label distinguishing voluntary vs error-triggered logouts */
  reason?: LogoutReason;
}

/**
 * Recognized hex values emitted with the CHANGE_THEME_COLOR event. Uppercase,
 * without the leading `#` — that is the format mobivery expects on the native
 * side. Closed-union so a typo at the call site fails to compile. Add new
 * values here if the dark-mode palette gains another shade.
 */
export type ThemeColor = '242426' | 'FFFFFF';

/**
 * Payload for CHANGE_THEME_COLOR event
 */
export interface ChangeThemeColorPayload {
  /** Hex without `#`, uppercase. */
  color: ThemeColor;
}
